/**
 * Updates the product-page Lead Time label from custom.availability_json
 * when an APO option with change_availability is selected.
 *
 * Data: window.MW_AVAILABILITY_JSON (product.metafields.custom.availability_json),
 * keyed by Shopify variant id (matches [data-mw-addon-variant-id]).
 * Standalone products use window.MW_PRODUCT_AVAILABILITY with the same fields.
 *
 * Unavailable, On Extended Backorder, and Discontinued grey out and disable
 * that option whether change_availability is true, false, or null.
 *
 * When change_availability is true, Shopify available inventory decides
 * which static field to show (fields are never overwritten):
 *   inventory > 0  -> product_availability  (Product Availability)
 *   inventory <= 0 -> default_availability  (Out of Stock Availability)
 * If the out-of-stock value is Discontinued, On Backorder, On Extended
 * Backorder, or Unavailable, that option is greyed out and disabled.
 *
 * Debug (product page console):
 *   MW_APO_AVAILABILITY.inspect('48662696853781')
 *   Or load with ?mw_avail_debug=1
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 50;
  var LABEL_SELECTOR =
    'label.product-availability-metafield, label.product-availability-metafield-red';
  var RED_VALUES = {
    Discontinued: true,
    Unavailable: true,
    'Out of Stock (More on the way!)': true,
    'On Backorder': true,
    'On Extended Backorder': true
  };
  var NO_ATC_VALUES = {
    Discontinued: true,
    Unavailable: true,
    'On Backorder': true,
    'On Extended Backorder': true
  };
  var DISABLE_OPTION_VALUES = {
    Discontinued: true,
    Unavailable: true,
    'On Backorder': true,
    'On Extended Backorder': true
  };
  // These statuses disable the option on their own. change_availability
  // (true, false, or null) does not keep the option selectable.
  var FORCE_DISABLE_VALUES = {
    Discontinued: true,
    Unavailable: true,
    'On Extended Backorder': true,
    'Extended Backorder': true
  };
  var DISABLED_OPTION_CLASS = 'mw-avail-option-disabled';

  var debounceTimer;
  var defaultState = null;
  var bound = false;
  var apoHooked = false;
  var appliedFromJson = false;
  var valuesReadyHandled = false;
  var lockedAvailability = null;
  var lockedVariantId = null;
  var inventoryByVariantId = {};
  var inventoryFetches = {};
  var pageInventorySeeded = false;

  function isDebugEnabled() {
    try {
      if (/[?&]mw_avail_debug=1(?:&|$)/.test(window.location.search)) return true;
      if (window.localStorage && window.localStorage.getItem('mw_avail_debug') === '1') {
        return true;
      }
    } catch (err) {}
    return false;
  }

  function debugLog() {
    if (!isDebugEnabled()) return;
    var args = ['[mw-apo-availability]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function getLookup() {
    var data = window.MW_AVAILABILITY_JSON;
    if (!data) return {};
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (err) {
        return {};
      }
    }
    return data;
  }

  function isTruthyFlag(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  function getShopifyId(gid) {
    return String(gid || '').split('/').pop();
  }

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function queryApoRoots() {
    var roots = [];
    var seen = [];
    document.querySelectorAll(
      '#mw-apo-app-block, .mw-apo-container, .mw-product-options'
    ).forEach(function (el) {
      if (seen.indexOf(el) !== -1) return;
      seen.push(el);
      roots.push(el);
    });
    return roots;
  }

  function optionIsIgnored(optionEl) {
    if (!optionEl) return true;
    if (optionEl.classList.contains('mw-hidden')) return true;
    if (optionEl.closest('.mw-apo-add-to-cart-cloned, sticky-atc-panel')) {
      return true;
    }
    return false;
  }

  function queryApoOptions() {
    var seen = {};
    var options = [];
    document.querySelectorAll('.mw-option').forEach(function (optionEl) {
      if (optionIsIgnored(optionEl)) return;
      var id = optionEl.getAttribute('data-option-id') || optionEl.id;
      if (id && seen[id]) return;
      if (id) seen[id] = true;
      options.push(optionEl);
    });
    return options;
  }

  function getOwnValueRows(optionEl) {
    var rows = [];
    optionEl.querySelectorAll('.mw-option__value').forEach(function (row) {
      if (row.closest('.mw-option') !== optionEl) return;
      rows.push(row);
    });
    return rows;
  }

  function getLabel() {
    return document.querySelector(LABEL_SELECTOR);
  }

  function captureDefault(label) {
    if (!label || defaultState) return;
    defaultState = {
      text: label.textContent,
      className: label.className,
      hidden: label.hidden
    };
  }

  function formatLabelText(availability) {
    if (!availability) return '';
    if (RED_VALUES[availability]) {
      if (availability === 'Out of Stock (More on the way!)') {
        return 'Out of Stock - Ships in ~2-3 Weeks';
      }
      if (availability === 'On Backorder') {
        return 'On Backorder - Ships in ~4-6 Weeks';
      }
      if (availability === 'On Extended Backorder') {
        return 'On Extended Backorder - Currently Unavailable';
      }
      return availability;
    }
    return 'Lead Time: ' + availability;
  }

  function setNoAtcVisibility(availability) {
    if (document.body.dataset.availabilityNoAtcLocked === '1') {
      document.body.classList.add('availability-no-atc');
      return;
    }

    if (availability && NO_ATC_VALUES[availability]) {
      document.body.classList.add('availability-no-atc');
    } else {
      document.body.classList.remove('availability-no-atc');
    }
  }

  function applyAvailability(availability) {
    var label = getLabel();
    if (!label) return;

    captureDefault(label);

    if (!availability) {
      restoreDefault();
      return;
    }

    var isRed = !!RED_VALUES[availability];
    label.className = isRed
      ? 'product-availability-metafield-red'
      : 'product-availability-metafield';
    label.textContent = formatLabelText(availability);
    label.hidden = false;
    setNoAtcVisibility(availability);
    appliedFromJson = true;
  }

  function restoreDefault() {
    var label = getLabel();
    if (!label || !defaultState) return;
    label.className = defaultState.className;
    label.textContent = defaultState.text;
    label.hidden = defaultState.hidden;
    setNoAtcVisibility(null);
    appliedFromJson = false;
  }

  function getLookupEntry(lookup, variantId) {
    if (!lookup || variantId == null || variantId === '') return null;
    var key = String(variantId);
    return lookup[key] || lookup[variantId] || null;
  }

  function entryDrivesAvailability(entry) {
    return !!(entry && isTruthyFlag(entry.change_availability));
  }

  function getProductLevelEntry() {
    var data = window.MW_PRODUCT_AVAILABILITY;
    if (!data || typeof data !== 'object') return null;
    return data;
  }

  function getCurrentProductVariantId() {
    var input = document.querySelector(
      '.js-product form[action="/cart/add"] [name="id"], form[action="/cart/add"] [name="id"]'
    );
    if (input && input.value) return String(input.value);
    if (window.MW_CURRENT_VARIANT_ID) return String(window.MW_CURRENT_VARIANT_ID);
    return '';
  }

  function resolveStandaloneAvailability() {
    var entry = getProductLevelEntry();
    if (!entryDrivesAvailability(entry)) return null;
    seedInventoryFromPage();
    return availabilityTextForEntry(entry, getCurrentProductVariantId());
  }

  function seedInventoryFromPage() {
    if (pageInventorySeeded) return;
    pageInventorySeeded = true;
    var data = window.MW_VARIANT_INVENTORY;
    if (!data || typeof data !== 'object') return;
    Object.keys(data).forEach(function (id) {
      setInventory(id, data[id], 'page');
    });
  }

  function setInventory(variantId, data, source) {
    if (variantId == null || variantId === '' || !data || typeof data !== 'object') return;
    var key = String(variantId);
    var existing = inventoryByVariantId[key];
    if (existing && existing.source === 'shopify' && source !== 'shopify') return;
    inventoryByVariantId[key] = {
      inventory_quantity: data.inventory_quantity,
      inventory_management: data.inventory_management,
      inventory_policy: data.inventory_policy,
      available: data.available,
      source: source || 'unknown'
    };
  }

  function getInventory(variantId) {
    if (variantId == null || variantId === '') return null;
    return inventoryByVariantId[String(variantId)] || null;
  }

  function isInventoryTracked(inv) {
    if (!inv) return false;
    var management = inv.inventory_management;
    if (management == null || management === false) return false;
    var text = String(management).trim().toLowerCase();
    return text !== '' && text !== 'null' && text !== 'none' && text !== 'false';
  }

  function inventoryQty(inv) {
    if (!inv || inv.inventory_quantity == null || inv.inventory_quantity === '') return null;
    var qty = Number(inv.inventory_quantity);
    return isFinite(qty) ? qty : null;
  }

  function availabilityChoiceForEntry(entry, variantId) {
    if (!entryDrivesAvailability(entry)) return null;

    var inStockText = normalizeText(entry.product_availability);
    var oosText = normalizeText(entry.default_availability);
    var inv = getInventory(variantId);
    var qty = inventoryQty(inv);
    var tracked = isInventoryTracked(inv);
    var useOos = tracked && qty != null && qty <= 0;
    var text = useOos ? (oosText || inStockText) : inStockText;

    if (!text) return null;

    return {
      text: text,
      field: useOos ? 'default_availability' : 'product_availability',
      qty: qty,
      tracked: tracked,
      inventoryLoaded: !!inv
    };
  }

  function availabilityTextForEntry(entry, variantId) {
    var choice = availabilityChoiceForEntry(entry, variantId);
    return choice ? choice.text : null;
  }

  function statusForcesDisable(value) {
    var text = normalizeText(value);
    if (!text || text.toLowerCase() === 'null') return false;
    return !!FORCE_DISABLE_VALUES[text];
  }

  function entryForcesOptionDisabled(entry) {
    if (!entry) return false;
    return (
      statusForcesDisable(entry.product_availability) ||
      statusForcesDisable(entry.check_availability)
    );
  }

  function optionShouldBeDisabled(entry, variantId) {
    if (entryForcesOptionDisabled(entry)) return true;
    if (!entryDrivesAvailability(entry)) return false;
    var choice = availabilityChoiceForEntry(entry, variantId);
    if (!choice || !choice.inventoryLoaded) return false;
    if (!choice.tracked || choice.qty == null || choice.qty > 0) return false;
    return !!DISABLE_OPTION_VALUES[choice.text];
  }

  function setNativeSelectValueDisabled(optionEl, valueId, disabled) {
    if (!optionEl || valueId == null || valueId === '') return;
    var matchId = String(valueId);
    optionEl.querySelectorAll('select option').forEach(function (opt) {
      if (String(opt.value) !== matchId) return;
      opt.disabled = disabled;
    });
    optionEl.querySelectorAll('.selectCustom-option').forEach(function (opt) {
      if (String(opt.getAttribute('data-value-id') || '') !== matchId) return;
      opt.classList.toggle(DISABLED_OPTION_CLASS, disabled);
      opt.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  function clearDisabledSelectIfChosen(optionEl) {
    if (!optionEl) return;
    var select = optionEl.querySelector('select');
    if (!select || select.selectedIndex < 0) return;
    var selected = select.options[select.selectedIndex];
    if (!selected || !selected.disabled) return;
    for (var i = 0; i < select.options.length; i++) {
      if (isPlaceholderSelectValue(select.options[i])) {
        select.selectedIndex = i;
        return;
      }
    }
    select.selectedIndex = -1;
  }

  function setRowDisabled(row, disabled) {
    if (!row) return;
    row.classList.toggle(DISABLED_OPTION_CLASS, disabled);
    row.setAttribute('aria-disabled', disabled ? 'true' : 'false');

    row.querySelectorAll(
      'input.mw-option__control, input[type="radio"], input[type="checkbox"]'
    ).forEach(function (input) {
      input.disabled = disabled;
      if (disabled && input.checked) {
        input.checked = false;
        row.classList.remove('mw-option__value--selected');
      }
    });

    var optionEl = row.closest('.mw-option');
    setNativeSelectValueDisabled(optionEl, row.getAttribute('data-value-id'), disabled);
    if (disabled) clearDisabledSelectIfChosen(optionEl);
  }

  function applyOptionDisabledStates() {
    var lookup = getLookup();
    queryApoOptions().forEach(function (optionEl) {
      getOwnValueRows(optionEl).forEach(function (row) {
        var variantId = getVariantIdFromRow(row);
        var entry = getLookupEntry(lookup, variantId);
        var disable = optionShouldBeDisabled(entry, variantId);
        setRowDisabled(row, disable);
        if (disable && lockedVariantId && String(lockedVariantId) === String(variantId)) {
          lockedVariantId = null;
          lockedAvailability = null;
        }
      });
    });
  }

  function getAddonHandleFromRow(row) {
    if (!row) return '';
    var fromAttr =
      row.getAttribute('data-mw-addon-handle') ||
      (row.dataset && row.dataset.mwAddonHandle) ||
      '';
    if (fromAttr) return String(fromAttr);

    var valueRecord = getApoValueRecord(row.getAttribute('data-value-id'));
    var value = getValueObject(valueRecord);
    var link = value && (value.link_variant || value.linkVariant);
    return (link && (link.handle || '')) || '';
  }

  function seedInventoryFromApoRow(row, variantId) {
    if (getInventory(variantId)) return;
    var valueRecord = getApoValueRecord(row && row.getAttribute('data-value-id'));
    var value = getValueObject(valueRecord);
    if (!value) return;

    var inv = value.inventory && typeof value.inventory === 'object' ? value.inventory : null;
    var qty =
      inv && inv.inventory_quantity != null
        ? inv.inventory_quantity
        : value.inventory_quantity;
    if (qty == null) return;

    setInventory(
      variantId,
      {
        inventory_quantity: qty,
        inventory_management:
          (inv && inv.inventory_management) || value.inventory_management || 'shopify',
        inventory_policy: (inv && inv.inventory_policy) || value.inventory_policy,
        available: Number(qty) > 0
      },
      'apo'
    );
  }

  function parseInventoryJson(text) {
    if (!text) return null;
    var trimmed = String(text).trim();
    var start = trimmed.indexOf('{');
    var end = trimmed.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) return null;
    try {
      return JSON.parse(trimmed.slice(start, end + 1));
    } catch (err) {
      try {
        return JSON.parse(
          trimmed.slice(start, end + 1).replace(/([{,]\s*)(\d+)\s*:/g, '$1"$2":')
        );
      } catch (err2) {
        return null;
      }
    }
  }

  function fetchInventoryForHandle(handle) {
    if (!handle) return Promise.resolve();
    if (inventoryFetches[handle]) return inventoryFetches[handle];

    inventoryFetches[handle] = fetch(
      '/products/' + encodeURIComponent(handle) + '?view=mw-inventory',
      { credentials: 'same-origin' }
    )
      .then(function (res) {
        return res.ok ? res.text() : '';
      })
      .then(function (text) {
        var data = parseInventoryJson(text);
        var variants = data && data.variants;
        if (!variants) return;
        Object.keys(variants).forEach(function (id) {
          setInventory(id, variants[id], 'shopify');
        });
      })
      .catch(function () {});

    return inventoryFetches[handle];
  }

  function collectInventoryHandles() {
    var lookup = getLookup();
    var handles = [];
    var seen = {};

    queryApoOptions().forEach(function (optionEl) {
      getOwnValueRows(optionEl).forEach(function (row) {
        var variantId = getVariantIdFromRow(row);
        var entry = getLookupEntry(lookup, variantId);
        if (!entryDrivesAvailability(entry)) return;

        seedInventoryFromApoRow(row, variantId);

        var handle = getAddonHandleFromRow(row);
        if (!handle || seen[handle]) return;
        seen[handle] = true;
        handles.push(handle);
      });
    });

    return handles;
  }

  function ensureInventory() {
    seedInventoryFromPage();
    var fetches = collectInventoryHandles().map(fetchInventoryForHandle);
    if (!fetches.length) return Promise.resolve();
    return Promise.all(fetches);
  }

  function forEachApoValueRecord(callback) {
    if (!window.mwApoInit || !mwApoInit.response) return;
    Object.keys(mwApoInit.response).forEach(function (productKey) {
      var productData = mwApoInit.response[productKey];
      var values = productData && productData.values;
      if (!values) return;
      Object.keys(values).forEach(function (valueId) {
        callback(valueId, values[valueId], productData);
      });
    });
  }

  function getApoValueRecord(valueId) {
    if (!valueId) return null;
    var id = String(valueId);
    var found = null;
    forEachApoValueRecord(function (key, record) {
      if (found) return;
      if (String(key) === id) {
        found = record;
        return;
      }
      var value = getValueObject(record);
      if (value && (String(value.id) === id || String(value.value_id) === id)) {
        found = record;
      }
    });
    return found;
  }

  function getValueObject(valueRecord) {
    if (!valueRecord) return null;
    if (valueRecord.value && typeof valueRecord.value === 'object') return valueRecord.value;
    return valueRecord;
  }

  function getVariantIdFromValue(value) {
    if (!value || typeof value !== 'object') return '';
    var link = value.link_variant || value.linkVariant || null;
    if (!link) return '';
    return getShopifyId(link.variant_id || link.variantId);
  }

  function getVariantIdFromRow(row) {
    if (!row) return '';
    var fromAttr =
      row.getAttribute('data-mw-addon-variant-id') ||
      (row.dataset && row.dataset.mwAddonVariantId) ||
      '';
    if (fromAttr) return String(fromAttr);

    var valueRecord = getApoValueRecord(row.getAttribute('data-value-id'));
    return getVariantIdFromValue(getValueObject(valueRecord));
  }

  function valueRowIsUsable(row) {
    if (!row) return false;
    if (row.classList.contains('mw-hidden')) return false;
    if (row.classList.contains('mw-option__value--not_available')) return false;
    if (row.classList.contains(DISABLED_OPTION_CLASS)) return false;
    var optionEl = row.closest('.mw-option');
    if (optionEl && optionEl.classList.contains('mw-hidden')) return false;
    return true;
  }

  function valueRowIsSelected(row) {
    if (!row) return false;

    // Radios/checkboxes: the checked input is the source of truth.
    // Mageworx often leaves --selected on a previous tile, which made a
    // later Size (10-12 weeks) overwrite X-Small (In Stock).
    var input = row.querySelector('input.mw-option__control, input[type="radio"], input[type="checkbox"]');
    if (input) return !!(input.checked && !input.disabled);

    if (row.classList.contains('mw-option__value--selected')) return true;
    if (row.getAttribute('aria-selected') === 'true') return true;
    if (row.getAttribute('aria-checked') === 'true') return true;
    if (row.classList.contains('is-selected') || row.classList.contains('selectCustom-option--selected')) {
      return true;
    }

    return false;
  }

  function getRowTitle(row) {
    if (!row) return '';
    var titles = row.querySelectorAll('.mw-stack--card__title, .mw-option__value-title, .mw-option__value-text');
    var el = titles.length ? titles[titles.length - 1] : row.querySelector('.mw-text');
    if (!el) return '';
    var clone = el.cloneNode(true);
    clone.querySelectorAll('.mws-text__sku, .mw-apo-value-sku, .mw-stack--card__radio').forEach(function (node) {
      node.remove();
    });
    return normalizeText(clone.textContent);
  }

  function getOptionTitle(optionEl) {
    if (!optionEl) return '';
    var el =
      optionEl.querySelector('.mw-option__title') ||
      optionEl.querySelector('.mw-collapsible--header_text') ||
      optionEl.querySelector('.mw-label > .mw-stack .mw-text');
    return normalizeText(el && el.textContent);
  }

  function findValueRowById(optionEl, valueId) {
    if (!optionEl || valueId == null || valueId === '') return null;
    var matchId = String(valueId);
    var rows = optionEl.querySelectorAll('.mw-option__value[data-value-id]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-value-id') === matchId) return rows[i];
    }
    return null;
  }

  function isPlaceholderSelectValue(optionEl) {
    if (!optionEl) return true;
    var text = normalizeText(optionEl.textContent);
    if (!optionEl.value || optionEl.value === '0') return true;
    return /^select(\s|$)/i.test(text) || /^choose(\s|$)/i.test(text);
  }

  function getSelectSelectedRow(optionEl) {
    var select = optionEl.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      var option = select.options[select.selectedIndex];
      if (option && !isPlaceholderSelectValue(option)) {
        var fromSelect = findValueRowById(optionEl, option.value);
        if (fromSelect) return fromSelect;
      }
    }

    var customSelected = optionEl.querySelector(
      '.selectCustom-option.is-selected, .selectCustom-option[aria-selected="true"], .selectCustom-option.selectCustom-option--selected'
    );
    if (customSelected) {
      if (customSelected.classList.contains('mw-option__value')) return customSelected;
      return customSelected.closest('.mw-option__value') || findValueRowById(
        optionEl,
        customSelected.getAttribute('data-value-id')
      );
    }

    return null;
  }

  function getAccumulatedSelectedRow(optionEl) {
    var acc = optionEl.querySelector('[data-mw-option-accumulated-title]');
    var text = normalizeText(acc && acc.textContent);
    if (!text || /^select/i.test(text) || /^none selected/i.test(text)) return null;
    text = text.replace(/\s*\([^)]*\$[^)]*\)\s*$/, '').trim();

    var match = null;
    optionEl.querySelectorAll('.mw-option__value').forEach(function (row) {
      var title = getRowTitle(row);
      if (!title) return;
      if (text === title || text.indexOf(title) === 0) match = row;
    });
    return match;
  }

  function getSelectedValueRows(optionEl) {
    var selected = [];
    var seen = [];

    function add(row) {
      if (!valueRowIsUsable(row)) return;
      if (seen.indexOf(row) !== -1) return;
      seen.push(row);
      selected.push(row);
    }

    getOwnValueRows(optionEl).forEach(function (row) {
      if (valueRowIsSelected(row)) add(row);
    });

    // If a radio/checkbox is actually checked, don't also pull a stale
    // hidden <select> value or accumulated-title substring match.
    if (selected.length) return selected;

    add(getSelectSelectedRow(optionEl));
    if (selected.length) return selected;

    add(getAccumulatedSelectedRow(optionEl));
    return selected;
  }

  function buildRowInfo(row, optionEl, usedRows, lookup) {
    var variantId = getVariantIdFromRow(row);
    var entry = getLookupEntry(lookup, variantId);
    var selectedDom = valueRowIsSelected(row);
    var used = usedRows.indexOf(row) !== -1;
    var choice = availabilityChoiceForEntry(entry, variantId);
    var inv = getInventory(variantId);
    return {
      option: getOptionTitle(optionEl),
      title: getRowTitle(row),
      valueId: row.getAttribute('data-value-id') || '',
      variantId: variantId,
      handle: getAddonHandleFromRow(row),
      selectedDom: selectedDom,
      used: used,
      usable: valueRowIsUsable(row),
      inLookup: !!entry,
      change: entry ? isTruthyFlag(entry.change_availability) : null,
      productAvailability: entry && entry.product_availability != null
        ? String(entry.product_availability)
        : null,
      defaultAvailability: entry && entry.default_availability != null
        ? String(entry.default_availability)
        : null,
      availability: choice ? choice.text : (entry && entry.product_availability != null
        ? String(entry.product_availability)
        : null),
      chosenField: choice ? choice.field : null,
      qty: choice ? choice.qty : inventoryQty(inv),
      tracked: choice ? choice.tracked : isInventoryTracked(inv),
      wouldApply: !!(used && choice)
    };
  }

  function buildReport(targetVariantId) {
    var lookup = getLookup();
    var label = getLabel();
    var rows = [];
    var matches = [];
    var selectedCount = 0;
    var valueRowCount = 0;
    var active = null;

    queryApoOptions().forEach(function (optionEl) {
      var selectedRows = getSelectedValueRows(optionEl);
      var usedRows = selectedRows;
      selectedCount += selectedRows.length;

      getOwnValueRows(optionEl).forEach(function (row) {
        valueRowCount += 1;
        var info = buildRowInfo(row, optionEl, usedRows, lookup);
        rows.push(info);
        if (info.wouldApply) {
          matches.push(info);
          active = info.availability;
        }
      });
    });

    var targetId = targetVariantId ? String(targetVariantId) : '';
    var targetRows = targetId
      ? rows.filter(function (row) { return row.variantId === targetId; })
      : [];

    return {
      labelText: label ? label.textContent : '(no label)',
      labelClass: label ? label.className : '',
      liquidDefault: defaultState ? defaultState.text : null,
      active: active,
      apoReady: !!(window.MWS_APO && window.MWS_APO.valuesReady),
      rootCount: queryApoRoots().length,
      lookupCount: lookup ? Object.keys(lookup).length : 0,
      lookupType: typeof window.MW_AVAILABILITY_JSON,
      valueRowCount: valueRowCount,
      selectedCount: selectedCount,
      matches: matches,
      targetId: targetId,
      targetInLookup: !!(targetId && getLookupEntry(lookup, targetId)),
      targetEntry: targetId ? getLookupEntry(lookup, targetId) : null,
      targetRows: targetRows,
      rows: rows
    };
  }

  function lockFromCheckedSelections() {
    if (lockedVariantId) return;
    var lookup = getLookup();

    queryApoOptions().some(function (optionEl) {
      return getOwnValueRows(optionEl).some(function (row) {
        var input = row.querySelector(
          'input.mw-option__control, input[type="radio"], input[type="checkbox"]'
        );
        if (!input || !input.checked || input.disabled) return false;

        var variantId = getVariantIdFromRow(row);
        var entry = getLookupEntry(lookup, variantId);
        if (!entryDrivesAvailability(entry)) return false;
        if (optionShouldBeDisabled(entry, variantId)) return false;

        lockedVariantId = variantId;
        lockedAvailability = availabilityTextForEntry(entry, variantId);
        console.log('[mw-apo-availability] locked on load', {
          option: getOptionTitle(optionEl),
          title: getRowTitle(row),
          variantId: variantId,
          availability: lockedAvailability,
          qty: inventoryQty(getInventory(variantId)),
          field: (availabilityChoiceForEntry(entry, variantId) || {}).field
        });
        return true;
      });
    });
  }

  function findActiveAvailability() {
    return buildReport().active || null;
  }

  function logReport(report) {
    console.group('[mw-apo-availability] inspect');
    console.log('label:', report.labelText);
    console.log('active:', report.active);
    console.log('apoReady:', report.apoReady, 'roots:', report.rootCount, 'lookup:', report.lookupCount, report.lookupType);
    console.log('value rows:', report.valueRowCount, 'dom-selected:', report.selectedCount);
    if (report.targetId) {
      console.log('target', report.targetId, {
        inLookup: report.targetInLookup,
        entry: report.targetEntry,
        inventory: getInventory(report.targetId),
        rows: report.targetRows
      });
    }
    if (report.matches.length) {
      console.log('would apply (last wins):');
      console.table(report.matches);
    } else {
      console.log('no change_availability match on a selected/default row');
    }
    console.log('all rows:');
    console.table(report.rows.map(function (row) {
      return {
        option: row.option,
        title: row.title,
        variantId: row.variantId,
        selectedDom: row.selectedDom,
        used: row.used,
        change: row.change,
        qty: row.qty,
        field: row.chosenField,
        disabled: optionShouldBeDisabled(
          getLookupEntry(getLookup(), row.variantId),
          row.variantId
        ),
        product_availability: row.productAvailability,
        default_availability: row.defaultAvailability,
        availability: row.availability
      };
    }));
    console.groupEnd();
  }

  function logSelectedRow(row, source) {
    if (!row) {
      console.log('[mw-apo-availability] selection (' + source + '): no value row');
      return;
    }
    var lookup = getLookup();
    var variantId = getVariantIdFromRow(row);
    var entry = getLookupEntry(lookup, variantId);
    console.log('[mw-apo-availability] selection (' + source + ')', {
      option: getOptionTitle(row.closest('.mw-option')),
      title: getRowTitle(row),
      variantId: variantId,
      valueId: row.getAttribute('data-value-id'),
      checked: !!(row.querySelector('input:checked')),
      selectedClass: row.classList.contains('mw-option__value--selected'),
      change_availability: entry ? entry.change_availability : '(not in lookup)',
      product_availability: entry ? entry.product_availability : '(not in lookup)',
      default_availability: entry ? entry.default_availability : '(not in lookup)',
      qty: inventoryQty(getInventory(variantId)),
      chosen: (availabilityChoiceForEntry(entry, variantId) || {}).field,
      lookupCount: Object.keys(lookup).length
    });
  }

  function resolveActiveAvailability() {
    var lookup = getLookup();
    var oosText = null;
    var inStockText = null;

    queryApoOptions().forEach(function (optionEl) {
      getSelectedValueRows(optionEl).forEach(function (row) {
        var variantId = getVariantIdFromRow(row);
        var choice = availabilityChoiceForEntry(
          getLookupEntry(lookup, variantId),
          variantId
        );
        if (!choice) return;
        if (choice.field === 'default_availability') oosText = choice.text;
        else inStockText = choice.text;
      });
    });

    if (oosText) return oosText;
    if (inStockText) return inStockText;
    var standalone = resolveStandaloneAvailability();
    if (standalone) return standalone;
    if (!lockedVariantId) return null;
    return availabilityTextForEntry(
      getLookupEntry(lookup, lockedVariantId),
      lockedVariantId
    );
  }

  function sync() {
    var label = getLabel();
    if (label) captureDefault(label);

    lockFromCheckedSelections();
    applyOptionDisabledStates();
    var report = buildReport();
    var availability = resolveActiveAvailability();
    lockedAvailability = availability || lockedAvailability;
    report.active = availability;
    debugLog('sync', {
      active: report.active,
      locked: lockedAvailability,
      lockedVariantId: lockedVariantId,
      label: report.labelText,
      selectedCount: report.selectedCount,
      valueRowCount: report.valueRowCount,
      apoReady: report.apoReady
    });

    if (availability) {
      if (label) applyAvailability(availability);
      else setNoAtcVisibility(availability);
      return report;
    }

    if (report.valueRowCount === 0) {
      return report;
    }

    // APO still hydrating — don't snap back to the Liquid default.
    if (!report.apoReady) {
      return report;
    }

    if (appliedFromJson && report.selectedCount === 0) {
      return report;
    }

    if (label) restoreDefault();
    else setNoAtcVisibility(null);
    return report;
  }

  function inspect(targetVariantId) {
    var report = buildReport(targetVariantId || '48662696853781');
    logReport(report);
    return report;
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      ensureInventory().then(sync);
    }, DEBOUNCE_MS);
  }

  function bindDocumentSelectionLog() {
    if (document.documentElement.dataset.mwApoAvailabilityLogBound) return;
    document.documentElement.dataset.mwApoAvailabilityLogBound = '1';

    document.addEventListener(
      'click',
      function (evt) {
        var blocked = evt.target && evt.target.closest
          ? evt.target.closest('.' + DISABLED_OPTION_CLASS)
          : null;
        if (!blocked) return;
        evt.preventDefault();
        evt.stopPropagation();
      },
      true
    );

    document.addEventListener(
      'change',
      function (evt) {
        var target = evt.target;
        if (!target || !target.classList || !target.classList.contains('mw-option__control')) {
          return;
        }

        var row = target.closest('.mw-option__value');
        if (row && row.classList.contains(DISABLED_OPTION_CLASS)) {
          evt.preventDefault();
          evt.stopPropagation();
          target.checked = false;
          return;
        }
        logSelectedRow(row, 'change');
        var lookup = getLookup();
        var variantId = getVariantIdFromRow(row);
        var entry = getLookupEntry(lookup, variantId);
        if (entryDrivesAvailability(entry) && !optionShouldBeDisabled(entry, variantId)) {
          lockedVariantId = variantId;
          lockedAvailability = availabilityTextForEntry(entry, variantId);
        }
        var report = sync();
        var label = getLabel();
        console.log('[mw-apo-availability] after change', {
          locked: lockedAvailability,
          lockedVariantId: lockedVariantId,
          qty: inventoryQty(getInventory(lockedVariantId)),
          active: report && report.active,
          labelNow: label ? label.textContent : '(no label)',
          matches: (report && report.matches || []).map(function (match) {
            return match.option + ': ' + match.title + ' = ' + match.availability;
          })
        });
      },
      true
    );
  }

  function bindRoots() {
    bindDocumentSelectionLog();
    queryApoRoots().forEach(function (root) {
      if (root.dataset.mwApoAvailabilityBound) return;
      root.dataset.mwApoAvailabilityBound = '1';
      root.addEventListener('change', scheduleSync, true);
      root.addEventListener('input', scheduleSync, true);
      root.addEventListener('click', scheduleSync, true);

      var obs = new MutationObserver(scheduleSync);
      obs.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-mw-addon-variant-id', 'data-mw-addon-handle']
      });
    });
  }

  function hookApoCallbacks() {
    if (apoHooked) return;
    window.mwApoInit = window.mwApoInit || {};
    var prevEvent = window.mwApoInit.onEvent;
    var prevCallback = window.mwApoInit.onCallback;

    window.mwApoInit.onEvent = function (event, args) {
      if (typeof prevEvent === 'function') prevEvent(event, args);
      if (event === 'init::finish') scheduleSync();
    };

    window.mwApoInit.onCallback = function (event, data) {
      if (typeof prevCallback === 'function') data = prevCallback(event, data);
      return data;
    };

    apoHooked = true;
  }

  function onValuesReady() {
    hookApoCallbacks();
    bindRoots();
    if (valuesReadyHandled) {
      scheduleSync();
      return;
    }
    valuesReadyHandled = true;
    ensureInventory().then(sync);
  }

  function boot() {
    if (bound) return;
    bound = true;

    hookApoCallbacks();
    document.addEventListener('mws:apo:values-ready', onValuesReady);

    if (window.MWS_APO && window.MWS_APO.valuesReady) {
      onValuesReady();
    } else if (entryDrivesAvailability(getProductLevelEntry())) {
      ensureInventory().then(sync);
    }
  }

  window.MW_APO_AVAILABILITY = {
    inspect: inspect,
    sync: sync,
    lookup: getLookup,
    inventory: function () { return inventoryByVariantId; }
  };

  seedInventoryFromPage();
  console.log('[mw-apo-availability] script loaded', {
    lookupCount: Object.keys(getLookup()).length,
    inventoryCount: Object.keys(inventoryByVariantId).length,
    hasLabel: !!getLabel()
  });

  bindDocumentSelectionLog();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', scheduleSync);
  document.addEventListener('mw-apo:auto-select', scheduleSync);
  document.addEventListener('on:variant:change', scheduleSync);
})();
